/**
 * Load schedules from a device for NextScheduleCard.
 * BLU TRV uses Trv.ListScheduleRules (with cached blutrv_rsched fallback);
 * Gen2+ uses schedule.list; Gen1 lights read settings.lights[channel].schedule_rules.
 */
import type { Device } from 'src/types/Device';
import type { Peripheral } from 'src/types/Peripherals';
import type { SchedulesG2Request } from 'src/types/requests/schedules/schedules_g2';
import { devices_settings } from 'src/stores/device-runtime/status';
import { filterScheduleMethodByChannel } from 'src/helpers/actions/schedules/supported/schedule_supported_helpers';
import { isMockLightDevice } from 'src/helpers/dev/mock_light_ids';
import { getScheduleList } from 'src/helpers/get_schedules_gen_2';
import { isBluTRV, is_gen_2, real_id } from 'src/helpers/interface_helpers';
import {
	bluTrvRulesToNextSchedule,
	gen1LightScheduleRev,
	gen1LightSchedulesFromSettings,
	parseScheduleJobs,
	schedulesFromBluTrvList,
	type NextScheduleRule,
} from 'src/helpers/next_schedule_helpers';
import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';

export type DeviceSchedulesFetchResult = {
	ok: boolean;
	schedules: NextScheduleRule[];
};

const schedulesForDeviceChannel = (
	schedules: NextScheduleRule[],
	device: Device,
): NextScheduleRule[] => {
	const deviceChannel = device.category === 'emeter' ? 0 : device.channel;
	return schedules.filter((schedule) =>
		schedule.calls.some((call) =>
			filterScheduleMethodByChannel(device, call, null as unknown as Peripheral, deviceChannel),
		),
	);
};

const gen1Channel = (device: Device): number => Number(device.channel) || 0;

const gen1Settings = (device: Device): Record<string, unknown> | undefined =>
	devices_settings.getByRealId(real_id(device.id));

export const getCachedDeviceSchedules = (device: Device): NextScheduleRule[] => {
	if (isMockLightDevice(device)) return [];
	if (isBluTRV(device.type)) {
		return bluTrvRulesToNextSchedule(getTrvComponentByDevice(device)?.schedules?.rules);
	}
	if (!is_gen_2(device)) {
		return gen1LightSchedulesFromSettings(gen1Settings(device), gen1Channel(device));
	}
	return [];
};

export const getDeviceScheduleRev = (device: Device): number | undefined => {
	if (isMockLightDevice(device)) return;
	if (isBluTRV(device.type)) {
		const rev = getTrvComponentByDevice(device)?.schedules?.schedule_rev;
		return typeof rev === 'number' ? rev : undefined;
	}
	if (!is_gen_2(device)) {
		return gen1LightScheduleRev(gen1Settings(device), gen1Channel(device));
	}
	return;
};

export const fetchDeviceSchedules = async (device: Device): Promise<DeviceSchedulesFetchResult> => {
	if (isMockLightDevice(device)) return { ok: true, schedules: [] };

	if (isBluTRV(device.type)) {
		const cached = getCachedDeviceSchedules(device);
		try {
			const res = await callBluTRV(device, 'Trv.ListScheduleRules', { id: 0 }, false, false);
			if (res?.isok)
				return {
					ok: true,
					schedules: schedulesFromBluTrvList(res.data),
				};
		} catch {
			// Fall through to the cached blutrv_rsched rules.
		}
		return { ok: false, schedules: cached };
	}

	if (!is_gen_2(device)) {
		return {
			ok: true,
			schedules: gen1LightSchedulesFromSettings(gen1Settings(device), gen1Channel(device)),
		};
	}

	try {
		const res: SchedulesG2Request | null = await getScheduleList(device);
		if (!res?.isok) return { ok: false, schedules: [] };
		return {
			ok: true,
			schedules: schedulesForDeviceChannel(parseScheduleJobs(res.data?.jobs || res.jobs), device),
		};
	} catch {
		return { ok: false, schedules: [] };
	}
};
